import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import { pick, get, merge, getOr } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Alert } from 'rsuite';
import { awsS3presignedRequest } from '../../services/fbm-api';
import showFailedRequestError from '../showFailedRequestError';
import { AppConfig } from '../config';

const FileSizeLimitInMB = 2;
const ImageSizeLimitInMB = 1;

const DefaultOptions = {
  autoProceed: false,
  restrictions: {
    maxFileSize: FileSizeLimitInMB * 1000000, // 2 MB
    maxNumberOfFiles: 1
  },
  locale: {
    strings: {
      dropHereOr: 'Drop file or %{browse}',
      browse: 'browse'
    }
  },
  onBeforeFileAdded(currentFile) {
    const fileSize = get('data.size', currentFile);
    const { maxFileSize } = this.restrictions;
    if (fileSize > maxFileSize) {
      Alert.error(
        `Max file size allowed is up to ${maxFileSize / 1000000} MB only.`,
        10000
      );
    }
  },
  note: `Accepted file size is up to ${FileSizeLimitInMB} MB only.` // Manually pass this prop to <DragDrop> component
};

const OverrideOptionsByType = {
  image: {
    restrictions: {
      maxFileSize: ImageSizeLimitInMB * 1000000, // 1 MB
      allowedFileTypes: ['image/*']
    },
    locale: {
      strings: {
        dropHereOr: 'Drop image or %{browse}',
        browse: 'browse'
      }
    },
    note: `Accepted image size is up to ${ImageSizeLimitInMB} MB only.`
  }
};

const uppyAwsS3Upload = ({
  type,
  bucket,
  baseDir = null,
  filename = null,
  fixedFilename = false,
  onFileAdded = () => {},
  onUpload = () => {},
  onUploadProgress = () => {},
  onUploadError = () => {},
  onComplete = () => {}
}) => {
  const optionsForType = getOr({}, type, OverrideOptionsByType);
  const uppyOptions = merge(DefaultOptions, optionsForType);
  return new Uppy(uppyOptions)
    .use(AwsS3, {
      async getUploadParameters({ name }) {
        const body = {
          bucket,
          baseDir,
          filename: filename || name,
          fixedFilename
        };
        const response = await awsS3presignedRequest({ body });
        if (get('status', response) === 'error') {
          showFailedRequestError(response);
          return null;
        }
        return pick(['method', 'url', 'fields'], response);
      }
    })
    .on('file-added', onFileAdded)
    .on('upload', onUpload)
    .on('upload-progress', onUploadProgress)
    .on('upload-error', onUploadError)
    .on('complete', onComplete);
};

uppyAwsS3Upload.propTypes = {
  type: PropTypes.oneOf(['image', 'file']),
  bucket: PropTypes.string,
  baseDir: PropTypes.string,
  onFileAdded: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onUploadProgress: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired
};

uppyAwsS3Upload.defaultProps = {
  type: 'file',
  bucket: get('buckets.files', AppConfig)
};

export default uppyAwsS3Upload;
